import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useCastEditor } from '../../hooks/useCastEditor';
import { r2Url } from '../../utils/r2Url';
import type {
  ProjectWithDetails,
  ReferenceImage,
  StoryElement,
  StoryElementKind,
} from '@historyjki/shared';
import { IMAGE_MODEL_PRICES_USD, resolveStylePromptFragment } from '@historyjki/shared';
import { Modal } from '../ui/Modal';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { ImageGallery } from '../ui/ImageGallery';
import { formatApproxUsd } from '../compose/SizePresetPicker';
import { flattenVisual, unFlattenVisual } from '../../utils/visualDescription';

const KIND_PLACEHOLDER: Record<StoryElementKind, string> = {
  character: '👤',
  place: '🏕️',
  object: '📦',
};

/** Reference sheets always generate on the pro model at its base size. */
const referenceCost = formatApproxUsd(
  IMAGE_MODEL_PRICES_USD['gemini-3-pro-image-preview'],
);

interface CastEditorModalProps {
  open: boolean;
  /** The story element being edited. */
  element: StoryElement;
  /** Project, used to read the element's reference images. */
  project: ProjectWithDetails;
  /** Persist edits to the element. Resolves true on success. */
  updateStoryElement: (elementId: string, data: {
    kind?: StoryElementKind;
    name?: string;
    description?: string;
    visual_description?: Record<string, unknown>;
  }) => Promise<boolean>;
  /** Delete the element. Omit the Delete button if not provided. */
  deleteStoryElement?: (elementId: string) => Promise<boolean>;
  /** Refetch project data after reference-image mutations. */
  refetch: () => Promise<unknown>;
  onClose: () => void;
  /** Called after a successful save (e.g. to refresh the parent view). */
  onSaved?: () => void;
  /** Navigate to the Illustration style tab — offered by the no-style
   *  nudge before generating an unstyled reference image. Omit where
   *  that navigation isn't available (e.g. Compose). */
  onGoToStyle?: () => void;
}

/**
 * Edit a cast element's visual definition and reference photos. Extracted
 * from the Define stage so the Illustrate stage can reuse the exact same
 * editing surface when a reference tile is clicked.
 */
export function CastEditorModal({
  open,
  element,
  project,
  updateStoryElement,
  deleteStoryElement,
  refetch,
  onClose,
  onSaved,
  onGoToStyle,
}: CastEditorModalProps) {
  const { showToast } = useToast();
  const {
    generatingPortrait,
    regeneratingRefIds,
    deletingRefIds,
    handleGeneratePortrait,
    handleRegenerate,
    handleRefUpload,
    handleRefDelete,
  } = useCastEditor({ element, project, refetch });

  const [name, setName] = useState(element.name);
  const [desc, setDesc] = useState(element.description ?? '');
  const [visualObj, setVisualObj] = useState<Record<string, string>>({});

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reference images are prompted with the project's art style; with no
  // style set they generate unstyled and then clash with everything drawn
  // after the style is picked — nudge before spending money on that.
  const hasStyle = resolveStylePromptFragment(project) != null;
  const [confirmNoStyle, setConfirmNoStyle] = useState(false);

  // Re-seed local form state whenever a different element is opened.
  useEffect(() => {
    setName(element.name);
    setDesc(element.description ?? '');
    let parsed: Record<string, unknown> = {};
    try {
      if (element.visual_description) {
        parsed = JSON.parse(element.visual_description) as Record<string, unknown>;
      }
    } catch {
      /* ignore malformed JSON */
    }
    setVisualObj(flattenVisual(parsed));
  }, [element.id, element.name, element.description, element.visual_description]);

  const kind = element.kind;
  const refImages: ReferenceImage[] = (project.reference_images || []).filter(
    (img) => img.owner_type === 'story_element' && img.owner_id === element.id,
  );
  // Last uploaded/generated ref doubles as the portrait, matching Define.
  const portrait = refImages.length > 0 ? refImages[refImages.length - 1] : null;

  const handleSave = async () => {
    const ok = await updateStoryElement(element.id, {
      kind,
      name,
      description: desc,
      visual_description: unFlattenVisual(visualObj),
    });
    if (!ok) return;
    showToast('Story element updated', 'success');
    onSaved?.();
    onClose();
  };

  const handleDelete = async () => {
    if (!deleteStoryElement) return;
    setDeleting(true);
    const ok = await deleteStoryElement(element.id);
    setDeleting(false);
    if (!ok) return;
    setConfirmDelete(false);
    onSaved?.();
    onClose();
  };

  const portraitBusy =
    generatingPortrait || (portrait != null && regeneratingRefIds.has(portrait.id));

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Edit ${kind} · ${element.name}`}
        size="lg"
        footer={
          <>
            {deleteStoryElement && (
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
            <div className="modal__foot-spacer" />
            {!element.reviewed_at && (
              <span className="cast-edit__review-status">Not yet reviewed</span>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave}>
              Save changes
            </Button>
          </>
        }
      >
        <div className="cast-edit">
          {/* Left: visual sidebar */}
          <div className="cast-edit__sidebar">
            <div className="cast-edit__portrait-wrap">
              {portrait ? (
                <img
                  src={r2Url(portrait.r2_key)}
                  alt={element.name}
                  className="cast-edit__portrait-img"
                />
              ) : (
                <div className="cast-edit__portrait-placeholder">{KIND_PLACEHOLDER[kind]}</div>
              )}
              <button
                type="button"
                className="cast-edit__regen"
                onClick={() => {
                  if (!hasStyle && !confirmNoStyle) {
                    setConfirmNoStyle(true);
                    return;
                  }
                  setConfirmNoStyle(false);
                  void (portrait ? handleRegenerate(portrait) : handleGeneratePortrait());
                }}
                disabled={portraitBusy}
                title={`Estimated cost per reference image: ${referenceCost}`}
              >
                {portraitBusy ? '✦ Generating…' : `✦ Regenerate · ${referenceCost}`}
              </button>
            </div>

            {portrait?.stale && (
              <div className="cast-edit__warning">
                <span>ⓘ</span>
                <span>
                  {portrait.stale_reason ?? 'Changed since this was drawn'} — regenerate to
                  keep later images consistent.
                </span>
              </div>
            )}

            {confirmNoStyle && !portraitBusy && (
              <div className="cast-edit__warning">
                <span>⚠</span>
                <span>
                  No art style is set yet — this image will generate unstyled and won't
                  match art drawn after you pick a style.{' '}
                  {onGoToStyle && (
                    <button
                      type="button"
                      className="cast-edit__warning-link"
                      onClick={() => {
                        setConfirmNoStyle(false);
                        onClose();
                        onGoToStyle();
                      }}
                    >
                      Set style first
                    </button>
                  )}
                  {onGoToStyle && ' or '}
                  <button
                    type="button"
                    className="cast-edit__warning-link"
                    onClick={() => {
                      setConfirmNoStyle(false);
                      void (portrait ? handleRegenerate(portrait) : handleGeneratePortrait());
                    }}
                  >
                    generate anyway
                  </button>
                  .
                </span>
              </div>
            )}

            <div className="cast-edit__refs">
              {refImages.map((img, idx) => {
                const deleting = deletingRefIds.has(img.id);
                return (
                  <div key={img.id} className="cast-edit__ref-thumb-wrap">
                    <button
                      type="button"
                      className="cast-edit__ref-thumb"
                      onClick={() => {
                        setGalleryIndex(idx);
                        setGalleryOpen(true);
                      }}
                    >
                      <img src={r2Url(img.r2_key)} alt="" />
                    </button>
                    <button
                      type="button"
                      className="cast-edit__ref-del"
                      aria-label="Delete reference image"
                      disabled={deleting}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleRefDelete(img);
                      }}
                    >
                      {deleting ? (
                        '…'
                      ) : (
                        <>
                          <span aria-hidden="true">×</span>
                          <span>Remove</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
              <label
                className={`cast-edit__add-ref${refImages.length === 0 ? ' cast-edit__add-ref--full' : ''}`}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) void handleRefUpload(files);
                    e.target.value = '';
                  }}
                />
                + Add ref
              </label>
            </div>
          </div>

          {/* Right: form fields */}
          <div className="cast-edit__fields">
            {!element.reviewed_at && (
              <div className="cast-edit__warning">
                <span>ⓘ</span>
                <span>
                  This {kind} hasn't been reviewed yet. Check the details and save to confirm.
                </span>
              </div>
            )}

            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Textarea
              label="Description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Describe this story element..."
            />

            {kind === 'character' && (
              <div className="cast-edit__visual-grid">
                <Input
                  label="Species"
                  value={visualObj.species ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, species: e.target.value }))}
                />
                <Input
                  label="Age"
                  value={visualObj.age ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, age: e.target.value }))}
                />
                <Input
                  label="Hair"
                  value={visualObj.hair ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, hair: e.target.value }))}
                />
                <Input
                  label="Clothing"
                  value={visualObj.clothing ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, clothing: e.target.value }))}
                />
                <div className="cast-edit__span-2">
                  <Input
                    label="Distinguishing features"
                    value={visualObj.distinguishing_features ?? ''}
                    onChange={(e) =>
                      setVisualObj((p) => ({ ...p, distinguishing_features: e.target.value }))
                    }
                    placeholder="e.g. scar on left cheek, bright green eyes"
                  />
                </div>
              </div>
            )}

            {kind === 'place' && (
              <>
                <Input
                  label="Setting type"
                  value={visualObj.setting_type ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, setting_type: e.target.value }))}
                  placeholder="e.g. forest, city, cave"
                />
                <Input
                  label="Atmosphere"
                  value={visualObj.atmosphere ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, atmosphere: e.target.value }))}
                  placeholder="e.g. misty, warm, eerie"
                />
                <Input
                  label="Distinguishing features"
                  value={visualObj.distinguishing_features ?? ''}
                  onChange={(e) =>
                    setVisualObj((p) => ({ ...p, distinguishing_features: e.target.value }))
                  }
                  placeholder="e.g. ancient stone arch, glowing mushrooms"
                />
              </>
            )}

            {kind === 'object' && (
              <>
                <Input
                  label="Material / size"
                  value={visualObj.material ?? ''}
                  onChange={(e) => setVisualObj((p) => ({ ...p, material: e.target.value }))}
                  placeholder="e.g. silver, palm-sized"
                />
                <Input
                  label="Distinguishing features"
                  value={visualObj.distinguishing_features ?? ''}
                  onChange={(e) =>
                    setVisualObj((p) => ({ ...p, distinguishing_features: e.target.value }))
                  }
                  placeholder="e.g. engraved initials, cracked handle"
                />
              </>
            )}

            <Textarea
              label="Art notes"
              value={visualObj.art_notes ?? ''}
              onChange={(e) => setVisualObj((p) => ({ ...p, art_notes: e.target.value }))}
              helper="Tone for the illustrator AI"
              placeholder="e.g. warm and slightly whimsical, avoid dark shadows"
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Rendered after the edit modal so it stacks above it. */}
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${kind}?`}
        message={`"${element.name}" and its reference images will be removed from this story.`}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />

      <ImageGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={refImages}
        initialIndex={galleryIndex}
      />
    </>
  );
}
